import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowLeft, Plus, Trash2, Camera, Upload, Send, Calculator,
  Loader2, Package, Wrench, IndianRupee, Image as ImageIcon, Mic, Square,
  Receipt, Building2, AlertCircle, Info, CheckCircle, XCircle, UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voice-to-text hook
const useVoiceInput = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-IN';

      recognitionRef.current.onresult = (event) => {
        const current = event.resultIndex;
        const result = event.results[current];
        setTranscript(result[0].transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied');
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current) {
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    } else {
      toast.error('Voice input not supported');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  return { isListening, transcript, startListening, stopListening, setTranscript };
};

// Voice Input Button Component
const VoiceInputButton = ({ onResult, placeholder, className = '', size = 'icon' }) => {
  const { isListening, transcript, startListening, stopListening, setTranscript } = useVoiceInput();

  useEffect(() => {
    if (transcript && !isListening) {
      onResult(transcript);
      setTranscript('');
    }
  }, [transcript, isListening, onResult, setTranscript]);

  return (
    <Button
      type="button"
      variant={isListening ? "destructive" : "outline"}
      size={size}
      onClick={isListening ? stopListening : startListening}
      className={`flex-shrink-0 ${isListening ? 'animate-pulse bg-red-500' : 'border-orange-500/50 text-orange-500 hover:bg-orange-500/10'} ${className}`}
      title={isListening ? 'Stop recording' : placeholder || 'Click to speak'}
    >
      {isListening ? <Square className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
    </Button>
  );
};

// Convert spoken numbers to digits
const parseSpokenNumber = (text) => {
  const numberWords = {
    'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4',
    'five': '5', 'six': '6', 'seven': '7', 'eight': '8', 'nine': '9',
    'ten': '10', 'eleven': '11', 'twelve': '12', 'thirteen': '13', 'fourteen': '14',
    'fifteen': '15', 'sixteen': '16', 'seventeen': '17', 'eighteen': '18', 'nineteen': '19',
    'twenty': '20', 'thirty': '30', 'forty': '40', 'fifty': '50',
    'sixty': '60', 'seventy': '70', 'eighty': '80', 'ninety': '90',
    'hundred': '00', 'thousand': '000', 'lakh': '00000',
    'oh': '0', 'o': '0'
  };
  
  let processed = text.toLowerCase();
  
  // Handle "X hundred" patterns
  processed = processed.replace(/(\w+)\s+hundred/g, (match, num) => {
    const digit = numberWords[num] || num;
    return digit + '00';
  });
  
  // Handle "X thousand" patterns
  processed = processed.replace(/(\w+)\s+thousand/g, (match, num) => {
    const digit = numberWords[num] || num;
    return digit + '000';
  });
  
  // Handle "X lakh" patterns
  processed = processed.replace(/(\w+)\s+lakh/g, (match, num) => {
    const digit = numberWords[num] || num;
    return digit + '00000';
  });
  
  // Convert remaining number words
  Object.entries(numberWords).forEach(([word, digit]) => {
    processed = processed.replace(new RegExp(`\\b${word}\\b`, 'g'), digit);
  });
  
  // Extract just the number
  const numbers = processed.match(/\d+/g);
  if (numbers) {
    return parseInt(numbers.join(''), 10);
  }
  return null;
};

const CreateEstimate = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { theme } = useTheme();
  const fileInputRef = useRef(null);
  const [job, setJob] = useState(null);
  const [garageProfile, setGarageProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [isGstInvoice, setIsGstInvoice] = useState(true);
  const [showManualApprovalModal, setShowManualApprovalModal] = useState(false);
  const [approvalItems, setApprovalItems] = useState([]);
  const [submittingApproval, setSubmittingApproval] = useState(false);

  useEffect(() => {
    fetchJob();
    fetchGarageProfile();
  }, [jobId]);

  const fetchGarageProfile = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/dashboard/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setGarageProfile(data.profile);
        // Default to GST invoice (user can always toggle)
      }
    } catch (error) {
      console.error('Failed to fetch garage profile');
    }
  };

  const fetchJob = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setJob(data.job);
        // Load existing estimate if present
        if (data.job.estimate?.line_items) {
          setLineItems(data.job.estimate.line_items);
          setNotes(data.job.estimate.notes || '');
          // Load GST invoice setting from existing estimate
          if (data.job.estimate.is_gst_invoice !== undefined) {
            setIsGstInvoice(data.job.estimate.is_gst_invoice);
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = (type) => {
    const newItem = {
      id: `item_${Date.now()}`,
      item_type: type,
      description: '',
      part_name: type === 'part' ? '' : null,
      part_number: type === 'part' ? '' : null,
      quantity: 1,
      unit_price: 0,
      gst_rate: type === 'labour' || type === 'body_shop_labour' ? 18 : 18, // Default GST rate
      hsn_code: type === 'part' ? '' : null,
      gst_detecting: false,
      proof_images: [],
      damage_images: [], // For body shop labour damage photos
      generate_explanation: type === 'part'
    };
    setLineItems([...lineItems, newItem]);
  };

  // AI-powered GST rate detection for parts
  const detectGstRate = async (index, partName) => {
    if (!partName || partName.length < 2) return;
    
    const token = localStorage.getItem('garage_token');
    const updated = [...lineItems];
    updated[index].gst_detecting = true;
    setLineItems(updated);
    
    try {
      const response = await fetch(`${API_URL}/api/garage/estimates/detect-gst-rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          part_name: partName,
          description: updated[index].description || ''
        })
      });
      
      const data = await response.json();
      
      if (data.success || data.gst_rate) {
        const updatedItems = [...lineItems];
        updatedItems[index].gst_rate = data.gst_rate;
        updatedItems[index].hsn_code = data.hsn_code;
        updatedItems[index].gst_detecting = false;
        
        // Recalculate amounts with new GST rate
        const item = updatedItems[index];
        const mrp = item.unit_price * item.quantity;
        
        if (isGstInvoice && item.item_type === 'part') {
          item.total_amount = mrp;
          item.base_amount = mrp / (1 + item.gst_rate / 100);
          item.gst_amount = mrp - item.base_amount;
        }
        
        setLineItems(updatedItems);
        toast.success(`GST Rate: ${data.gst_rate}% (HSN: ${data.hsn_code})`);
      }
    } catch (error) {
      console.error('GST detection failed:', error);
      const updatedItems = [...lineItems];
      updatedItems[index].gst_detecting = false;
      setLineItems(updatedItems);
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // Recalculate amounts based on GST invoice setting
    const item = updated[index];
    const mrp = item.unit_price * item.quantity;
    
    if (isGstInvoice) {
      if (item.item_type === 'labour') {
        // Labour: GST added on top
        item.base_amount = mrp;
        item.gst_amount = mrp * (item.gst_rate / 100);
        item.total_amount = mrp + item.gst_amount;
      } else {
        // Parts: Back-calculate GST from MRP
        item.total_amount = mrp;
        item.base_amount = mrp / (1 + item.gst_rate / 100);
        item.gst_amount = mrp - item.base_amount;
      }
    } else {
      // Non-GST: No tax calculation
      item.base_amount = mrp;
      item.gst_amount = 0;
      item.total_amount = mrp;
    }
    
    setLineItems(updated);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0 || currentItemIndex === null) return;

    const token = localStorage.getItem('garage_token');
    const currentItem = lineItems[currentItemIndex];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', currentItem.item_type === 'body_shop_labour' ? 'damage' : 'proof');
      
      try {
        const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}/images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await response.json();
        
        if (data.success) {
          const updated = [...lineItems];
          if (currentItem.item_type === 'body_shop_labour') {
            // Add to damage_images for body shop items
            if (!updated[currentItemIndex].damage_images) {
              updated[currentItemIndex].damage_images = [];
            }
            if (updated[currentItemIndex].damage_images.length < 4) {
              updated[currentItemIndex].damage_images.push(data.image.url);
              toast.success('Damage photo uploaded');
            } else {
              toast.error('Maximum 4 damage photos allowed');
            }
          } else {
            // Add to proof_images for parts
            updated[currentItemIndex].proof_images.push(data.image.url);
            toast.success('Proof image uploaded');
          }
          setLineItems(updated);
        }
      } catch (error) {
        toast.error('Failed to upload image');
      }
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const calculateTotals = () => {
    if (isGstInvoice) {
      // GST Invoice: Show base amounts and GST separately
      const subtotalParts = lineItems
        .filter(i => i.item_type === 'part')
        .reduce((sum, i) => sum + (i.base_amount || (i.unit_price * i.quantity) / 1.18), 0);
      
      // Labour includes both regular labour and body shop labour
      const subtotalLabour = lineItems
        .filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour')
        .reduce((sum, i) => sum + (i.base_amount || i.unit_price * i.quantity), 0);
      
      const partsGst = lineItems
        .filter(i => i.item_type === 'part')
        .reduce((sum, i) => sum + (i.gst_amount || 0), 0);
      
      const labourGst = lineItems
        .filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour')
        .reduce((sum, i) => sum + (i.gst_amount || subtotalLabour * 0.18), 0);
      
      const totalGst = partsGst + labourGst;
      const grandTotal = subtotalParts + subtotalLabour + totalGst;
      
      return { subtotalParts, subtotalLabour, totalGst, grandTotal, partsGst, labourGst };
    } else {
      // Non-GST Invoice: Simple totals without tax breakdown
      const subtotalParts = lineItems
        .filter(i => i.item_type === 'part')
        .reduce((sum, i) => sum + (i.unit_price * i.quantity), 0);
      
      const subtotalLabour = lineItems
        .filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour')
        .reduce((sum, i) => sum + (i.unit_price * i.quantity), 0);
      
      const grandTotal = subtotalParts + subtotalLabour;
      
      return { subtotalParts, subtotalLabour, totalGst: 0, grandTotal, partsGst: 0, labourGst: 0 };
    }
  };

  const handleSaveEstimate = async () => {
    if (lineItems.length === 0) {
      toast.error('Add at least one item');
      return;
    }

    // Validate body shop items have at least 1 damage photo
    const bodyShopWithoutPhotos = lineItems.filter(
      item => item.item_type === 'body_shop_labour' && (!item.damage_images || item.damage_images.length === 0)
    );
    if (bodyShopWithoutPhotos.length > 0) {
      toast.error('Body shop items require at least 1 damage photo');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('garage_token');
    const totals = calculateTotals();

    try {
      const response = await fetch(`${API_URL}/api/garage/estimates/${jobId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          line_items: lineItems.map(item => ({
            ...item,
            generate_explanation: item.generate_explanation && item.item_type === 'part'
          })),
          notes,
          is_gst_invoice: isGstInvoice,
          ...totals
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Estimate saved!');
        fetchJob();
      } else {
        toast.error(data.detail || 'Failed to save estimate');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendToCustomer = async () => {
    setSending(true);
    const token = localStorage.getItem('garage_token');

    try {
      const response = await fetch(`${API_URL}/api/garage/estimates/${jobId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Estimate sent to customer!');
        // Update job status to quote_raised
        await fetch(`${API_URL}/api/garage/jobs/${jobId}/status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'quote_raised' })
        });
        navigate(`/garage/jobs/${jobId}`);
      } else {
        toast.error(data.detail || 'Failed to send estimate');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSending(false);
    }
  };

  // Open Manual Approval Modal
  const openManualApprovalModal = () => {
    // Initialize approval items from current line items
    const items = lineItems.map(item => ({
      item_id: item.id,
      part_name: item.part_name || item.description || 'Item',
      item_type: item.item_type,
      quantity: item.quantity,
      total_amount: item.total_amount || (item.unit_price * item.quantity),
      current_status: item.status || 'pending',
      new_status: item.status || 'pending'
    }));
    setApprovalItems(items);
    setShowManualApprovalModal(true);
  };

  // Toggle item approval status
  const toggleItemApproval = (index, status) => {
    const updated = [...approvalItems];
    updated[index].new_status = status;
    setApprovalItems(updated);
  };

  // Submit manual approvals
  const handleSubmitManualApproval = async () => {
    // Get only items that have been changed to approved or rejected
    const itemsToUpdate = approvalItems
      .filter(item => item.new_status === 'approved' || item.new_status === 'rejected')
      .map(item => ({
        item_id: item.item_id,
        status: item.new_status
      }));

    if (itemsToUpdate.length === 0) {
      toast.error('Please approve or reject at least one item');
      return;
    }

    setSubmittingApproval(true);
    const token = localStorage.getItem('garage_token');

    try {
      const response = await fetch(`${API_URL}/api/garage/estimates/${jobId}/manual-approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: itemsToUpdate })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setShowManualApprovalModal(false);
        // Refresh job data to get updated estimate
        fetchJob();
      } else {
        toast.error(data.detail || 'Failed to update approvals');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSubmittingApproval(false);
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="create-estimate-page" className={`min-h-screen ${theme.pageBg} transition-colors duration-200`}>
      {/* Header */}
      <header className={`${theme.cardBg} border-b ${theme.border} px-4 py-3`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/garage/jobs/${jobId}`)}
              className={`${theme.textSecondary} hover:${theme.textPrimary}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={`text-lg font-bold ${theme.textPrimary}`}>Create Estimate</h1>
              <p className={`text-xs ${theme.textSecondary}`}>{job?.job_number} • {job?.customer_name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Customer Complaint Reference */}
        {job?.customer_complaint?.text && (
          <Card className={`${theme.infoBg} border`}>
            <CardContent className="p-4">
              <p className={`text-sm ${theme.infoText} font-medium mb-1`}>Customer Complaint:</p>
              <p className={theme.textPrimary}>{job.customer_complaint.text}</p>
            </CardContent>
          </Card>
        )}

        {/* GST Invoice Toggle */}
        <Card className={`${theme.cardBg} border ${theme.border}`} data-testid="gst-invoice-toggle-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isGstInvoice 
                    ? 'bg-blue-500/20 text-blue-400' 
                    : `${theme.pageBg} ${theme.textSecondary}`
                }`}>
                  <Receipt className="w-5 h-5" />
                </div>
                <div>
                  <Label htmlFor="gst-toggle" className={`font-medium ${theme.textPrimary} cursor-pointer`}>
                    Invoice Type
                  </Label>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    {garageProfile?.gst_number 
                      ? `GSTIN: ${garageProfile.gst_number}` 
                      : 'No GST number registered'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${!isGstInvoice ? theme.textPrimary : theme.textMuted}`}>
                  Non-GST
                </span>
                <Switch
                  id="gst-toggle"
                  checked={isGstInvoice}
                  onCheckedChange={(checked) => {
                    setIsGstInvoice(checked);
                    // Recalculate all line items when toggle changes
                    lineItems.forEach((_, idx) => {
                      updateLineItem(idx, 'gst_rate', lineItems[idx].gst_rate);
                    });
                  }}
                  data-testid="gst-toggle"
                />
                <span className={`text-sm font-medium ${isGstInvoice ? theme.textPrimary : theme.textMuted}`}>
                  GST
                </span>
              </div>
            </div>
            
            {/* Info about GST calculation */}
            <div className={`mt-3 p-3 rounded-lg ${
              isGstInvoice
                ? 'bg-blue-500/10 border border-blue-500/30'
                : `${theme.pageBg}`
            }`}>
              {isGstInvoice ? (
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-300">
                    <p className="font-medium">GST Invoice Selected</p>
                    <p className="text-blue-400/80 mt-1">
                      • Parts: AI detects GST rate (5%/12%/18%/28%) based on HSN code<br/>
                      • Labour: 18% GST added on top of base amount
                    </p>
                    {!garageProfile?.gst_number && (
                      <p className="mt-2 text-yellow-400">
                        Note: Add GST number in Garage Profile to display GSTIN on invoice
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertCircle className={`w-4 h-4 ${theme.textSecondary} mt-0.5 flex-shrink-0`} />
                  <div className={`text-sm ${theme.textSecondary}`}>
                    <p className="font-medium">Non-GST Invoice</p>
                    <p className="mt-1">
                      • Parts: MRP price as-is (no tax breakdown)<br/>
                      • Labour: No GST applied
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Add Item Buttons */}
        <div className="flex gap-3 flex-wrap">
          <Button 
            onClick={() => addLineItem('part')}
            className="flex-1 min-w-[140px] bg-green-600 hover:bg-green-700"
          >
            <Package className="w-4 h-4 mr-2" /> Add Part
          </Button>
          <Button 
            onClick={() => addLineItem('labour')}
            className="flex-1 min-w-[140px] bg-blue-600 hover:bg-blue-700"
          >
            <Wrench className="w-4 h-4 mr-2" /> Add Labour
          </Button>
          <Button 
            onClick={() => addLineItem('body_shop_labour')}
            className="flex-1 min-w-[140px] bg-purple-600 hover:bg-purple-700"
            data-testid="add-body-shop-btn"
          >
            <Camera className="w-4 h-4 mr-2" /> Body Shop
          </Button>
        </div>

        {/* Line Items */}
        <div className="space-y-4">
          {lineItems.map((item, index) => {
            const itemColors = {
              part: { border: 'border-l-green-500', bg: 'bg-green-500/5', text: 'text-green-400', label: '🔧 Part' },
              labour: { border: 'border-l-blue-500', bg: 'bg-blue-500/5', text: 'text-blue-400', label: '👨‍🔧 Labour' },
              body_shop_labour: { border: 'border-l-purple-500', bg: 'bg-purple-500/5', text: 'text-purple-400', label: '🛠️ Body Shop' }
            };
            const colors = itemColors[item.item_type] || itemColors.labour;
            
            return (
            <Card key={item.id} className={`border-l-4 ${colors.border} ${colors.bg} ${theme.border}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${colors.text}`}>
                    {colors.label}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLineItem(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {item.item_type === 'part' && (
                    <>
                      <div>
                        <Label className={`${theme.textSecondary} text-xs`}>Part Name</Label>
                        <div className="flex gap-1">
                          <Input
                            value={item.part_name || ''}
                            onChange={(e) => updateLineItem(index, 'part_name', e.target.value)}
                            onBlur={(e) => {
                              if (isGstInvoice && e.target.value && e.target.value.length >= 2) {
                                detectGstRate(index, e.target.value);
                              }
                            }}
                            placeholder="e.g., Brake Pad"
                            className={`${theme.inputBg} ${theme.textPrimary}`}
                          />
                          <VoiceInputButton 
                            onResult={(text) => {
                              updateLineItem(index, 'part_name', text);
                              if (isGstInvoice && text.length >= 2) {
                                setTimeout(() => detectGstRate(index, text), 500);
                              }
                            }}
                            placeholder="Speak part name"
                          />
                        </div>
                        {/* GST Rate Badge for Parts */}
                        {isGstInvoice && (
                          <div className="flex items-center gap-2 mt-1">
                            {item.gst_detecting ? (
                              <span className="text-xs text-blue-400 flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Detecting GST rate...
                              </span>
                            ) : (
                              <Badge 
                                className={`text-xs cursor-pointer ${
                                  item.gst_rate === 28 ? 'bg-red-600' :
                                  item.gst_rate === 18 ? 'bg-blue-600' :
                                  item.gst_rate === 12 ? 'bg-green-600' :
                                  'bg-emerald-600'
                                }`}
                                onClick={() => {
                                  // Cycle through GST rates on click
                                  const rates = [5, 12, 18, 28];
                                  const currentIdx = rates.indexOf(item.gst_rate);
                                  const nextRate = rates[(currentIdx + 1) % rates.length];
                                  updateLineItem(index, 'gst_rate', nextRate);
                                }}
                                title="Click to change GST rate"
                              >
                                GST: {item.gst_rate}%
                                {item.hsn_code && ` (HSN: ${item.hsn_code})`}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className={`${theme.textSecondary} text-xs`}>Part Number</Label>
                        <div className="flex gap-1">
                          <Input
                            value={item.part_number || ''}
                            onChange={(e) => updateLineItem(index, 'part_number', e.target.value)}
                            placeholder="OEM/Aftermarket"
                            className={`${theme.inputBg} ${theme.textPrimary}`}
                          />
                          <VoiceInputButton 
                            onResult={(text) => updateLineItem(index, 'part_number', text.toUpperCase().replace(/\s+/g, ''))}
                            placeholder="Speak part number"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className={item.item_type === 'part' ? 'col-span-2' : 'col-span-2'}>
                    <Label className={`${theme.textSecondary} text-xs`}>Description</Label>
                    <div className="flex gap-1">
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        placeholder={
                          item.item_type === 'part' ? 'Why this part needs replacement' : 
                          item.item_type === 'body_shop_labour' ? 'Body work description (e.g., Dent removal on rear door)' :
                          'Labour description'
                        }
                        className={`${theme.inputBg} ${theme.textPrimary}`}
                      />
                      <VoiceInputButton 
                        onResult={(text) => updateLineItem(index, 'description', text)}
                        placeholder="Speak description"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className={`${theme.textSecondary} text-xs`}>Quantity</Label>
                    <div className="flex gap-1">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className={`${theme.inputBg} ${theme.textPrimary}`}
                      />
                      <VoiceInputButton 
                        onResult={(text) => {
                          const num = parseSpokenNumber(text);
                          if (num && num > 0) updateLineItem(index, 'quantity', num);
                        }}
                        placeholder="Speak quantity"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className={`${theme.textSecondary} text-xs`}>
                      Unit Price {item.item_type === 'part' ? '(MRP)' : '(Base)'}
                    </Label>
                    <div className="flex gap-1">
                      <div className="relative flex-1">
                        <IndianRupee className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
                        <Input
                          type="number"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className={`pl-9 ${theme.inputBg} ${theme.textPrimary}`}
                        />
                      </div>
                      <VoiceInputButton 
                        onResult={(text) => {
                          const num = parseSpokenNumber(text);
                          if (num !== null && num >= 0) updateLineItem(index, 'unit_price', num);
                        }}
                        placeholder="Speak price"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className={`${theme.textSecondary} text-xs`}>Total</Label>
                    <div className={`h-10 flex items-center px-3 ${theme.pageBg} rounded-md`}>
                      <span className={`${theme.textPrimary} font-medium`}>
                        ₹{(item.total_amount || item.unit_price * item.quantity).toLocaleString()}
                      </span>
                      {isGstInvoice && (
                        <span className={`text-xs ${theme.textSecondary} ml-1`}>
                          {item.item_type === 'part' ? '(incl. GST)' : '(+GST)'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Proof Images for Parts */}
                {item.item_type === 'part' && (
                  <div>
                    <Label className={`${theme.textSecondary} text-xs mb-2 block`}>
                      Faulty Part Proof (Required)
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {item.proof_images.map((img, imgIdx) => {
                        const imgUrl = img.startsWith('/uploads/') ? `/api${img}` : img;
                        return (
                          <div key={imgIdx} className="w-16 h-16 rounded-lg overflow-hidden border border-green-500/50">
                            <img src={`${API_URL}${imgUrl}`} alt="Proof" className="w-full h-full object-cover" />
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentItemIndex(index);
                          fileInputRef.current?.click();
                        }}
                        className={`w-16 h-16 rounded-lg border-2 border-dashed ${theme.border} hover:border-green-500 flex items-center justify-center transition-colors`}
                      >
                        <Camera className={`w-5 h-5 ${theme.textSecondary}`} />
                      </button>
                    </div>
                    {item.proof_images.length === 0 && (
                      <p className="text-xs text-yellow-500 mt-1">⚠️ Add photo proof for customer trust</p>
                    )}
                  </div>
                )}

                {/* Damage Images for Body Shop Labour */}
                {item.item_type === 'body_shop_labour' && (
                  <div>
                    <Label className={`${theme.textSecondary} text-xs mb-2 block`}>
                      Damage Photos (1-4 required)
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {(item.damage_images || []).map((img, imgIdx) => {
                        const imgUrl = img.startsWith('/uploads/') ? `/api${img}` : img;
                        return (
                          <div key={imgIdx} className="w-16 h-16 rounded-lg overflow-hidden border border-purple-500/50 relative group">
                            <img src={`${API_URL}${imgUrl}`} alt="Damage" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...lineItems];
                                updated[index].damage_images = updated[index].damage_images.filter((_, i) => i !== imgIdx);
                                setLineItems(updated);
                              }}
                              className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                      {(item.damage_images?.length || 0) < 4 && (
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentItemIndex(index);
                            fileInputRef.current?.click();
                          }}
                          className={`w-16 h-16 rounded-lg border-2 border-dashed border-purple-500/50 hover:border-purple-500 flex items-center justify-center transition-colors`}
                        >
                          <Camera className="w-5 h-5 text-purple-400" />
                        </button>
                      )}
                    </div>
                    {(item.damage_images?.length || 0) === 0 && (
                      <p className="text-xs text-red-400 mt-1">⚠️ At least 1 damage photo required for body shop work</p>
                    )}
                    <p className={`text-xs ${theme.textMuted} mt-1`}>
                      {item.damage_images?.length || 0}/4 photos • Document the damage before work begins
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })}
        </div>

        {lineItems.length === 0 && (
          <Card className={`${theme.cardBg} border ${theme.border}`}>
            <CardContent className="py-12 text-center">
              <Calculator className={`w-12 h-12 mx-auto mb-3 ${theme.textMuted}`} />
              <p className={theme.textSecondary}>No items added yet</p>
              <p className={`text-sm ${theme.textMuted}`}>Add parts or labour to create an estimate</p>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className={`${theme.textSecondary} text-xs`}>Notes for Customer</Label>
              <VoiceInputButton 
                onResult={(text) => setNotes(prev => prev ? `${prev} ${text}` : text)}
                placeholder="Speak notes"
                size="sm"
                className="h-7 w-7"
              />
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes or recommendations..."
              className={`${theme.inputBg} ${theme.textPrimary}`}
              rows={2}
            />
          </CardContent>
        </Card>

        {/* Totals */}
        {lineItems.length > 0 && (
          <Card className={`bg-gradient-to-r ${
            isGstInvoice
              ? 'from-blue-500/10 to-indigo-500/10 border-blue-500/30'
              : 'from-orange-500/10 to-red-500/10 border-orange-500/30'
          }`}>
            <CardContent className="p-4 space-y-2">
              {/* Invoice Type Badge */}
              <div className="flex justify-between items-center mb-3">
                <span className={`${theme.textSecondary} text-sm`}>Estimate Summary</span>
                <Badge className={isGstInvoice ? 'bg-blue-600' : 'bg-slate-600'}>
                  {isGstInvoice ? 'GST Invoice' : 'Non-GST Invoice'}
                </Badge>
              </div>
              
              {isGstInvoice ? (
                <>
                  {/* GST Invoice Breakdown */}
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>Parts (Base Amount)</span>
                    <span className={theme.textPrimary}>₹{Math.round(totals.subtotalParts).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>GST on Parts</span>
                    <span className={theme.textPrimary}>₹{Math.round(totals.partsGst || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>Labour (Base Amount)</span>
                    <span className={theme.textPrimary}>₹{Math.round(totals.subtotalLabour).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>GST on Labour (18%)</span>
                    <span className={theme.textPrimary}>₹{Math.round(totals.labourGst || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-blue-500/30">
                    <span className={theme.textSecondary}>Total GST</span>
                    <span className="text-blue-400 font-medium">₹{Math.round(totals.totalGst).toLocaleString()}</span>
                  </div>
                </>
              ) : (
                <>
                  {/* Non-GST Invoice - Simple totals */}
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>Parts (MRP)</span>
                    <span className={theme.textPrimary}>₹{Math.round(totals.subtotalParts).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>Labour</span>
                    <span className={theme.textPrimary}>₹{Math.round(totals.subtotalLabour).toLocaleString()}</span>
                  </div>
                </>
              )}
              
              <div className={`flex justify-between text-xl font-bold border-t pt-3 mt-2 ${
                isGstInvoice ? 'border-blue-500/30' : 'border-orange-500/30'
              }`}>
                <span className={theme.textPrimary}>Grand Total</span>
                <span className={isGstInvoice ? 'text-blue-400' : 'text-orange-400'}>
                  ₹{Math.round(totals.grandTotal).toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={handleSaveEstimate}
            disabled={saving || lineItems.length === 0}
            className="flex-1 min-w-[120px] bg-slate-600 hover:bg-slate-500"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Draft
          </Button>
          <Button
            onClick={handleSendToCustomer}
            disabled={sending || lineItems.length === 0 || !job?.estimate}
            className="flex-1 min-w-[120px] bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send to Customer
          </Button>
          <Button
            onClick={openManualApprovalModal}
            disabled={lineItems.length === 0 || !job?.estimate}
            className="flex-1 min-w-[120px] bg-emerald-600 hover:bg-emerald-700"
            data-testid="manual-approval-btn"
          >
            <UserCheck className="w-4 h-4 mr-2" />
            Manual Approval
          </Button>
        </div>

        {/* Manual Approval Modal */}
        <Dialog open={showManualApprovalModal} onOpenChange={setShowManualApprovalModal}>
          <DialogContent className={`${theme.cardBg} ${theme.textPrimary} max-w-2xl max-h-[85vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-500" />
                Manual Estimate Approval
              </DialogTitle>
              <DialogDescription className={theme.textSecondary}>
                Approve or reject individual items. Use this when customer has given verbal confirmation or is present at the garage.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 my-4">
              {approvalItems.map((item, index) => {
                const itemColors = {
                  part: { bg: 'bg-green-500/10', border: 'border-green-500/30', icon: Package, label: 'Part' },
                  labour: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: Wrench, label: 'Labour' },
                  body_shop_labour: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Camera, label: 'Body Shop' }
                };
                const colors = itemColors[item.item_type] || itemColors.labour;
                const IconComponent = colors.icon;

                return (
                  <div 
                    key={item.item_id} 
                    className={`p-4 rounded-lg border ${colors.border} ${colors.bg}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.bg}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${theme.textPrimary}`}>
                            {item.part_name}
                          </p>
                          <p className={`text-sm ${theme.textSecondary}`}>
                            {colors.label} • Qty: {item.quantity} • ₹{Math.round(item.total_amount).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant={item.new_status === 'approved' ? 'default' : 'outline'}
                          onClick={() => toggleItemApproval(index, item.new_status === 'approved' ? 'pending' : 'approved')}
                          className={item.new_status === 'approved' 
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                            : `${theme.border} hover:bg-emerald-500/20 hover:text-emerald-400`}
                          data-testid={`approve-item-${index}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant={item.new_status === 'rejected' ? 'default' : 'outline'}
                          onClick={() => toggleItemApproval(index, item.new_status === 'rejected' ? 'pending' : 'rejected')}
                          className={item.new_status === 'rejected' 
                            ? 'bg-red-600 hover:bg-red-700 text-white' 
                            : `${theme.border} hover:bg-red-500/20 hover:text-red-400`}
                          data-testid={`reject-item-${index}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>

                    {/* Status indicator */}
                    {item.current_status !== 'pending' && item.new_status === item.current_status && (
                      <div className="mt-2 flex items-center gap-1">
                        <Badge 
                          className={item.current_status === 'approved' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-red-500/20 text-red-400'}
                        >
                          Previously {item.current_status}
                        </Badge>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className={`p-3 rounded-lg ${theme.pageBg} border ${theme.border}`}>
              <div className="flex justify-between text-sm">
                <span className={theme.textSecondary}>Approved:</span>
                <span className="text-emerald-400 font-medium">
                  {approvalItems.filter(i => i.new_status === 'approved').length} items
                </span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className={theme.textSecondary}>Rejected:</span>
                <span className="text-red-400 font-medium">
                  {approvalItems.filter(i => i.new_status === 'rejected').length} items
                </span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className={theme.textSecondary}>Pending:</span>
                <span className={`${theme.textMuted} font-medium`}>
                  {approvalItems.filter(i => i.new_status === 'pending').length} items
                </span>
              </div>
            </div>

            <DialogFooter className="gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowManualApprovalModal(false)}
                className={theme.border}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitManualApproval}
                disabled={submittingApproval || approvalItems.every(i => i.new_status === 'pending')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-manual-approval"
              >
                {submittingApproval ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Confirm Approval
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleImageUpload}
        />
      </main>
    </div>
  );
};

export default CreateEstimate;
